import { FC, useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
	Box,
	Text,
	Button,
	Flex,
	HStack,
	Avatar,
} from 'src/components/ui';
import {
	ICreative,
	isCreatomateAttributes,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import UserContext from 'src/contexts/user/UserContext';
import { CampaignContext } from 'src/contexts';
import CreativeCard from '../CreativeCard';
import MiniGlobeIcon from 'src/assets/icons/MiniGlobe';
import { IChannelCreativeAttributes } from 'src/lib/schemas';
import { formatCallToAction } from '../utils/formatedCTA';
import FusionLoading from 'src/components/common/FusionLoading';
import TemplatePreview, { TemplatePreviewHandle } from 'src/components/templates/TemplatePreview';
import { FaPlay, FaPause, FaExpand } from 'react-icons/fa';
import VideoExpandModal from '../utils/VideoExpandModal';
import useSmoothProgress from '../utils/useSmoothProgress';

interface VideoSquareCreativeProps {
	creative: ICreative;
	withActions?: boolean;
	size?: string;
	creativesConfig?: IChannelCreativeAttributes[];
	isPaid?: boolean;
}

const VideoSquareCreative: FC<VideoSquareCreativeProps> = ({
	creative,
	withActions = true,
	size = 'md',
	creativesConfig,
	isPaid,
}) => {
	const { account } = useContext(UserContext);
	const { campaign } = useContext(CampaignContext);
	const destination = campaign?.destination ?? '';
	const { primaryText, headline, callToAction } = (creative as any).attributes;
	const [logo, setLogo] = useState<string | undefined>(account?.logoDark || account?.logoSquare);
	const previewRef = useRef<TemplatePreviewHandle>(null);
	const [previewReady, setPreviewReady] = useState(false);
	const [hasStarted, setHasStarted] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isHovering, setIsHovering] = useState(false);
	const [duration, setDuration] = useState(10);
	const seekBarRef = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const { progressBarRef, timeDisplayRef, onTimeChange, setTimeImmediate } = useSmoothProgress({ duration, isPlaying });

	const onPreviewReady = useCallback(() => {
		setPreviewReady(true);
		setTimeout(() => {
			if (previewRef.current) {
				previewRef.current.setLoop(false);
				previewRef.current.setControls(false);
				previewRef.current.setTime(5);
				setDuration(previewRef.current.getDuration() || 10);
			}
		}, 300);
	}, []);

	// Auto-correct duration if the SDK reports a larger playback time than the cached value
	// (happens when getDuration() returns a partial value right after onReady).
	const handleTimeChange = useCallback((time: number) => {
		if (time > duration + 0.1) {
			setDuration(time);
		}
		onTimeChange(time);
	}, [duration, onTimeChange]);

	const isPending =
		creative.status === 'pending' || creative.status === 'generating';
	const cleanedDomain = destination
		.replace(/(^\w+:|^)\/\//, '')
		.replace(/^www\./, '')
		.split('/')[0];

	const isCreatomate =
		creative.adEngine === 'creatomate' &&
		isCreatomateAttributes(creative.attributes);

	const videoTemplate = useMemo(() => {
		if (isCreatomate) {
			return JSON.parse(JSON.stringify(creative.attributes));
		}
		return null;
	}, [JSON.stringify((creative as any).attributes.elements)]);

	const handlePlayPause = useCallback(() => {
		if (!previewRef.current) return;
		if (isPlaying) {
			previewRef.current.pause();
		} else {
			previewRef.current.play();
		}
	}, [isPlaying]);

	const handleInitialPlay = useCallback(() => {
		if (!previewRef.current) return;
		setHasStarted(true);
		previewRef.current.setTime(0);
		previewRef.current.play();
	}, []);

	const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (!seekBarRef.current || !previewRef.current) return;
		const rect = seekBarRef.current.getBoundingClientRect();
		const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
		const newTime = ratio * duration;
		previewRef.current.setTime(newTime);
		setTimeImmediate(newTime);
	}, [duration, setTimeImmediate]);

	const onPlay = useCallback(() => setIsPlaying(true), []);
	const onPause = useCallback(() => setIsPlaying(false), []);

	useEffect(() => {
		setLogo(account?.logoDark || account?.logoSquare);
	}, [account]);

	return (
		<>
		<CreativeCard
			creative={creative}
			withActions={withActions}
			size={size}
			title="Video Feed"
			creativesConfig={creativesConfig}
		>
			{isPending ? (
				<Flex width="100%" minH={{ base: '400px', md: '756px' }} justifyContent="center">
					<FusionLoading isLoading />
				</Flex>
			) : (
				<>
					<Box p={4}>
						<Flex alignItems="center" mb={4} gap={2}>
							<Avatar
								key={logo || 'no-logo'}
								src={logo}
								sx={{ img: { objectFit: 'contain' } }}
							/>
							<Box>
								<Text fontWeight="bold">{account?.name}</Text>
								<HStack spacing={1}>
									{isPaid && (
										<>
											<Text color="black" fontSize="sm">
												Sponsored ·
											</Text>
											<MiniGlobeIcon />
										</>
									)}
								</HStack>
							</Box>
						</Flex>
						<Text mb={2} noOfLines={2} overflow="hidden">
							{primaryText}
						</Text>
						<Text color="blue.500" isTruncated>
							{destination}
						</Text>
					</Box>
					{isCreatomate && videoTemplate ? (
						<Box
							position="relative"
							h={{ base: 'auto', sm: '382px' }}
							onMouseEnter={() => setIsHovering(true)}
							onMouseLeave={() => setIsHovering(false)}
						>
							<Box filter={previewReady && !hasStarted ? 'brightness(0.7)' : undefined} h="100%">
								<TemplatePreview
									ref={previewRef}
									key={creative.id}
									template={videoTemplate}
									onReady={onPreviewReady}
									onTimeChange={handleTimeChange}
									onPlay={onPlay}
									onPause={onPause}
								/>
							</Box>
							{/* Initial centered play button - before user starts */}
							{previewReady && !hasStarted && (
								<Flex
									position="absolute"
									top={0}
									left={0}
									right={0}
									bottom={0}
									align="center"
									justify="center"
									zIndex={2}
									cursor="pointer"
									onClick={handleInitialPlay}
								>
									<Flex
										w="64px"
										h="64px"
										borderRadius="full"
										bg="blackAlpha.600"
										align="center"
										justify="center"
										boxShadow="lg"
									>
										<Box as={FaPlay} color="white" fontSize="24px" ml="4px" />
									</Flex>
								</Flex>
							)}
							{/* Video controls bar - show on hover after started */}
							{hasStarted && isHovering && (
								<Flex
									position="absolute"
									bottom={0}
									left={0}
									right={0}
									bg="blackAlpha.700"
									px={3}
									py={2}
									align="center"
									gap={2}
									zIndex={2}
								>
									<Box
										as="button"
										onClick={handlePlayPause}
										color="white"
										cursor="pointer"
										flexShrink={0}
										_hover={{ opacity: 0.8 }}
									>
										<Box as={isPlaying ? FaPause : FaPlay} fontSize="14px" ml={isPlaying ? '0' : '2px'} />
									</Box>
									<Box
										ref={seekBarRef}
										flex={1}
										h="6px"
										bg="whiteAlpha.300"
										borderRadius="full"
										cursor="pointer"
										onClick={handleSeek}
										position="relative"
									>
										<Box
											h="100%"
											bg="white"
											borderRadius="full"
											ref={progressBarRef}
											w="0%"
										/>
									</Box>
									<Text ref={timeDisplayRef} color="white" fontSize="xs" flexShrink={0} whiteSpace="nowrap">
										0:00 / 0:00
									</Text>
									<Box
										as="button"
										onClick={() => setIsExpanded(true)}
										color="white"
										cursor="pointer"
										flexShrink={0}
										_hover={{ opacity: 0.8 }}
									>
										<Box as={FaExpand} fontSize="12px" />
									</Box>
								</Flex>
							)}
						</Box>
					) : (
						<Flex w="100%" minH="382px" align="center" justify="center" bg="gray.100">
							<FusionLoading isLoading />
						</Flex>
					)}
					<HStack
						p={4}
						bg="gray.50"
						borderTop="1px solid"
						borderColor="gray.200"
						borderBottomRadius="lg"
					>
						<Box maxW="70%">
							<Text fontSize="sm" color="gray.500" isTruncated>
								{cleanedDomain}
							</Text>
							<Text
								data-testid="creative-headline"
								fontWeight="bold"
								noOfLines={1}
							>
								{headline}
							</Text>
							<Text fontSize="sm" color="gray.500" noOfLines={2}>
								{primaryText}
							</Text>
						</Box>
						{callToAction && (
							<Button
								size="sm"
								cursor="default"
								bg="gray.100"
								minW="fit-content"
								px={3}
								_hover={{ bg: 'gray.100' }}
								fontWeight={500}
								ml="auto"
							>
								{formatCallToAction(callToAction)}
							</Button>
						)}
					</HStack>
				</>
			)}
		</CreativeCard>
		{isExpanded && videoTemplate && (
			<VideoExpandModal
				isOpen={isExpanded}
				onClose={() => setIsExpanded(false)}
				template={videoTemplate}
				creativeId={creative.id}
			/>
		)}
		</>
	);
};

export default VideoSquareCreative;
